import { create, StateCreator } from 'zustand'
import { CookieHelper } from 'Utils/cookieHelper'
import { PARAMS_COOKIE_KEYS } from 'Consts/cookies.enum'
import { UserProfileResponse } from 'Features/userProfile/api/getUserProfile'
import { devtools } from 'zustand/middleware'
import { LanguagesTypes } from 'Types/Languages'

const { getLogicCookieData } = CookieHelper()

export interface UserStore {
  isLoggedIn: boolean
  isLoading: boolean
  token: string
  owner: boolean
  userId: number
  accountId: number
  subscriptionId: number | null
  networkId: string
  isAdmin: boolean
  theme: 'Dark' | 'Light'
  language: LanguagesTypes
  email: string
  businessEmail: boolean
  phoneNumber: string
  fullName: string
  accountName: string
  timezoneCountry: string
  multiAccount: boolean
  forceNoCc?: boolean
  isSignUpPage: boolean
  isInstallmentsSubscription: boolean
  isTrialSubscription: boolean
  reactivationPrevPlans: Array<number>
  isReactivation: boolean
  origin: string
  paymentMethod: string

  logIn: (
    token: string,
    userId: number,
    accountId: number,
    networkId: string,
    owner: boolean,
    subscriptionId: number | null,
    accountName: string,
    isReactivation: boolean,
    multiAccount?: boolean,
    userVerificationCodeRequired?: boolean
  ) => void
  logOut: () => void
  changeAccount: (
    accountId: number,
    networkId: string,
    owner: boolean,
    accountName: string,
    subscriptionId: number,
    isInstallmentsSubscription?: boolean
  ) => void
  setToken: (token: string) => void
  setSubscription: (subscriptionId: number | null) => void
  setInstallmentsSubscription: (isInstallmentsSubscription: boolean) => void
  setTheme: (theme: 'Light' | 'Dark') => void
  setProfile: (profile: UserProfileResponse) => void
  setLanguage: (language: LanguagesTypes) => void
  setForceCc: (forceNoCc: boolean) => void
  setSignUp: (isSignUpPage: boolean) => void
  setIsLoggedIn: (isLoggedIn: boolean) => void
  setReactivationPrevPlans: (prevPlans: Array<number>) => void
  setOrigin: (origin: string) => void
  setPaymentMethod: (paymentMethod: string) => void
  setIsReactivation: (isReactivation: boolean) => void
}

const userStore = (set: (fn: () => Partial<UserStore>) => void) => ({
  isLoggedIn: !!parseInt(getLogicCookieData(PARAMS_COOKIE_KEYS.ACCOUNT_ID)),
  isLoading: false,
  token: '',
  language: 'en',
  theme: 'Light',
  userId: parseInt(getLogicCookieData(PARAMS_COOKIE_KEYS.USER_ID)),
  owner: getLogicCookieData(PARAMS_COOKIE_KEYS.OWNER) === 'true',
  accountId: parseInt(getLogicCookieData(PARAMS_COOKIE_KEYS.ACCOUNT_ID)) || 0,
  networkId: getLogicCookieData(PARAMS_COOKIE_KEYS.NETWORK_ID),
  isAdmin: getLogicCookieData(PARAMS_COOKIE_KEYS.IS_ADMIN) === 'true',
  email: '',
  accountName: getLogicCookieData(PARAMS_COOKIE_KEYS.ACCOUNT_NAME),
  subscriptionId: null,
  businessEmail: false,
  phoneNumber: '',
  fullName: '',
  timezoneCountry: '',
  multiAccount: false,
  forceNoCc: false,
  isSignUpPage: false,
  isInstallmentsSubscription: false,
  isTrialSubscription: false,
  reactivationPrevPlans: [],
  isReactivation: false,
  origin: '',
  paymentMethod: '',
  logIn: (
    token: string,
    userId: number,
    accountId: number,
    networkId: string,
    owner: boolean,
    subscriptionId: number | null,
    accountName: string,
    isReactivation: boolean,
    multiAccount?: boolean,
    userVerificationCodeRequired?: boolean
  ) => {
    set(() => ({
      token,
      userId,
      accountId,
      accountName,
      networkId,
      isLoggedIn: !userVerificationCodeRequired,
      subscriptionId,
      owner,
      multiAccount: multiAccount || false,
      isReactivation,
    }))
  },
  logOut: () => {
    set(() => ({
      token: '',
      userId: 0,
      accountId: 0,
      networkId: '',
      subscriptionId: null,
      isLoggedIn: false,
      owner: false,
      isAdmin: false,
      origin: '',
      paymentMethod: '',
    }))
  },
  setSubscription: (subscriptionId: number | null) => {
    set(() => ({
      subscriptionId,
    }))
  },
  setInstallmentsSubscription: (isInstallmentsSubscription: boolean) => {
    set(() => ({
      isInstallmentsSubscription,
    }))
  },
  changeAccount: (
    accountId: number,
    networkId: string,
    owner: boolean,
    accountName: string,
    subscriptionId: number
  ) => {
    set(() => ({
      accountId,
      networkId,
      owner,
      accountName,
      subscriptionId,
    }))
  },
  setToken: (token: string) => {
    set(() => ({
      token,
    }))
  },
  setTheme: (theme: 'Dark' | 'Light') => {
    set(() => ({
      theme,
    }))
  },
  setLanguage: (language: LanguagesTypes) => {
    set(() => ({
      language,
    }))
  },
  setProfile: (profile: UserProfileResponse) => {
    set(() => ({
      email: profile?.email,
      businessEmail: profile?.businessEmail,
      phoneNumber: profile?.phoneNumber,
      fullName: profile?.fullName,
      timezoneCountry: profile?.timezoneCountry,
    }))
  },
  setIsLoggedIn: (isLoggedIn: boolean) => {
    set(() => ({
      isLoggedIn,
    }))
  },
  setSignUp: (isSignUpPage: boolean) => {
    set(() => ({
      isSignUpPage,
    }))
  },
  setForceCc: (forceNoCc: boolean) => {
    set(() => ({
      forceNoCc,
    }))
  },
  setReactivationPrevPlans: (prevPlans: Array<number>) => {
    set(() => ({
      reactivationPrevPlans: prevPlans,
    }))
  },
  setOrigin: (origin: string) => {
    set(() => ({
      origin,
    }))
  },
  setPaymentMethod: (paymentMethod: string) => {
    set(() => ({
      paymentMethod,
    }))
  },
  setIsReactivation: (isReactivation: boolean) => {
    set(() => ({
      isReactivation,
    }))
  },
})

const useUserStore = create<UserStore>()(
  devtools(userStore as StateCreator<UserStore, [['zustand/devtools', never]], [], UserStore>)
)
export default useUserStore
